import React, { useCallback, useMemo } from "react";
import {
  HotelCfarHtsMerchandising,
  useDeviceTypes,
  HtsMobileContinueButton,
} from "halifax";
import { CfarOfferType } from "redmond/apis/tysons/ancillaries";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedCfarId,
  setSelectedHtsCfarOfferId,
} from "../../../actions/actions";
import {
  DO_NOT_APPLY_OPTION_KEY,
  selectedHtsCfarOfferIdSelector,
} from "../../../reducer";
import { isCfarOptionSelectionCompleteSelector } from "../../../../shop/reducer";
import type { HtsCfarRateQuoteSuccess } from "../utils";
import { getSelectedAccountReferenceId } from "../../../../rewards/reducer";

export const Cap1HotelCfarHtsMerchandising = (props: {
  cfarRateQuote: HtsCfarRateQuoteSuccess;
  onLoadSuccess?: () => void;
  onContinue?: () => void;
}) => {
  const dispatch = useDispatch();
  const { matchesMobile } = useDeviceTypes();
  const isCfarSelected = useSelector(isCfarOptionSelectionCompleteSelector);
  const selectedHtsCfarOfferId = useSelector(selectedHtsCfarOfferIdSelector);
  const selectedAccountReferenceId = useSelector(getSelectedAccountReferenceId);
  const isMobileContinueButtonVisible = matchesMobile && isCfarSelected;
  const { cfarRateQuote, onLoadSuccess, onContinue } = props;

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    }
  };

  const selectNoOffer = useCallback(
    (unselectedHtsCfarOfferIdValue?: string) => {
      dispatch(
        setSelectedCfarId({
          value: DO_NOT_APPLY_OPTION_KEY,
          offerType: CfarOfferType.Hts,
        })
      );
      if (unselectedHtsCfarOfferIdValue) {
        dispatch(setSelectedHtsCfarOfferId(unselectedHtsCfarOfferIdValue));
      }
    },
    [dispatch]
  );

  const { rewardsType, rewardsConversion } = useMemo(() => {
    if (!selectedAccountReferenceId) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    const rewardsPremiumAmount =
      cfarRateQuote.premiumAmount.accountSpecific?.[selectedAccountReferenceId];

    if (
      !rewardsPremiumAmount ||
      rewardsPremiumAmount.value === 0 ||
      cfarRateQuote.premiumAmount.fiat.value === 0
    ) {
      return { rewardsType: undefined, rewardsConversion: undefined };
    }

    return {
      rewardsType: rewardsPremiumAmount.currency.toLowerCase(),
      rewardsConversion:
        rewardsPremiumAmount.value / cfarRateQuote.premiumAmount.fiat.value,
    };
  }, [cfarRateQuote, selectedAccountReferenceId]);

  const merchandisingUrlString = cfarRateQuote.merchandisingUrl;

  const merchandisingUrl = useMemo(() => {
    if (!merchandisingUrlString) {
      return null;
    }
    const url = new URL(merchandisingUrlString);

    if (rewardsType && rewardsConversion) {
      url.searchParams.set("rewardsType", rewardsType);
      url.searchParams.set("rewardsConversion", rewardsConversion.toString());
    }

    return url;
  }, [merchandisingUrlString, rewardsType, rewardsConversion]);

  if (!merchandisingUrl) {
    return null;
  }

  return (
    <div style={{ marginBottom: isMobileContinueButtonVisible ? "60px" : 0 }}>
      <HotelCfarHtsMerchandising
        merchandisingUrl={merchandisingUrl}
        onSelect={(eventData) => {
          if (eventData.attached) {
            dispatch(
              setSelectedCfarId({
                value: cfarRateQuote.id,
                offerType: CfarOfferType.Hts,
              })
            );
            dispatch(setSelectedHtsCfarOfferId(eventData.selected));
          } else {
            selectNoOffer(eventData.selected);
          }
        }}
        onLoadError={selectNoOffer}
        onLoadSuccess={onLoadSuccess}
        isLoading={false}
        isError={false}
        offerId={cfarRateQuote.id}
        selected={selectedHtsCfarOfferId}
      />

      {isMobileContinueButtonVisible && (
        <HtsMobileContinueButton onClick={handleContinue} />
      )}
    </div>
  );
};
